import React from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import { iDollarSign, iEye, iTrendingUp } from '@hover/icons';
import { Body, Link } from '@hover/blueprint';
import type { IHoverPros } from '../interfaces/IHoverPros';
import type { SolutionTestimonyItem } from '../interfaces/ISolutionTestimony';
import type { ISolutionPoints } from '../interfaces/ISolutionPoints';
import type { ISolutionPartnerTableProps } from '../interfaces/ISolutionsPartners';
import type { ILearningCardProps } from '../components/common/learning-card';

export const ESTIMATE_AND_CLOSE_PROS: IHoverPros = [
  {
    description: 'One simple, visual tool for sales to follow',
    icon: iEye,
    key: 1,
  },
  {
    description: 'Detailed quotes that are right the first time',
    icon: iDollarSign,
    key: 2,
  },
  {
    description: 'Remove the risk before it hits production',
    icon: iTrendingUp,
    key: 3,
  },
];

export const ESTIMATE_AND_CLOSE_TESTIMONIES: SolutionTestimonyItem[] = [
  {
    quote:
      'HOVER has made the previously difficult process of accurately measuring and pricing roofing and siding projects simple. It takes all the anxiety away and all the rep has to do is demonstrate the product and sell to the customer',
    name: 'Tim Lengal, Director of Operations',
    role: 'Feldco',
    image: (
      <StaticImage
        src="../assets/images/estimate-and-close_testimony_1.png"
        alt=""
        objectFit="contain"
      />
    ),
    logo: (
      <StaticImage
        src="../assets/images/estimate-and-close_testimony_logo_1.png"
        alt="J&B"
      />
    ),
    id: 1,
  },
  {
    quote:
      'The Sales rep just follows the prompts and it tells us how many boxes of nails, how many tubes of caulk are needed with the price for each and every item, plus the overhead and profit… everything!',
    name: 'Charlie wallace, General Manager',
    role: 'royalty roofinga',
    image: (
      <StaticImage
        src="../assets/images/estimate-and-close_testimony_2.png"
        objectFit="contain"
        alt=""
      />
    ),
    logo: (
      <StaticImage
        src="../assets/images/estimate-and-close_testimony_logo_2.png"
        alt="J&B"
      />
    ),
    id: 2,
  },
];

export const ESTIMATE_AND_CLOSE_VISUALLY_SELECT_POINTS: ISolutionPoints['items'] =
  [
    {
      id: 1,
      content: 'Measurements seamlessly integrated with your estimation',
    },
    {
      id: 2,
      content:
        'Simple questions about the project so you can customize your estimate',
    },
    {
      id: 3,
      content:
        'Quickly select materials and colors from suppliers and manufacturers you work with',
    },
    {
      id: 4,
      content: 'Easily define scope by selecting sections on the 3D model',
    },
    {
      id: 5,
      content:
        'Estimate in the field or the office with our native mobile app or from the web',
    },
  ];

export const ESTIMATE_AND_CLOSE_NUMBERS_POINTS: ISolutionPoints['items'] = [
  {
    id: 1,
    content: 'To-the-penny pricing with takeoffs calculated for you',
  },
  {
    id: 2,
    content: 'Automatically calculate material and labor costs',
  },
  {
    id: 3,
    content: 'Controlled margins baked into every quote',
  },
  {
    id: 4,
    content: (
      <Body flex={1} size={500}>
        Production-ready detail for&nbsp;
        <Link href="/direct-ordering">material ordering</Link>.
      </Body>
    ),
  },
  {
    id: 5,
    content:
      'Custom setup and training with industry experts for your unique business needs',
  },
];

export const ESTIMATE_AND_CLOSE_PROPOSALS_POINTS: ISolutionPoints['items'] = [
  {
    id: 1,
    content:
      'Supports multiple trades, with siding, roofing, facia, gutters, and more in a single estimate',
  },
  {
    id: 2,
    content:
      'Automatically turn estimates into professional proposals with easy sharing and digital signatures',
  },
  {
    id: 3,
    content: 'Quickly change budget line items for material or labor costs',
  },
  {
    id: 4,
    content:
      'Universal access to every job, whether your in sales, production, or management',
  },
];

export const ESTIMATE_IMPROVEMENT_PARTNERS: ISolutionPartnerTableProps['partners'] =
  [
    {
      key: 1,
      logo: (
        <StaticImage
          src="../assets/images/mc-clellands_logo.png"
          alt="McClellands Contracting and Roofing LLC"
        />
      ),
    },
    {
      key: 2,
      logo: (
        <StaticImage
          src="../assets/images/gouge-roofing_logo.png"
          alt="Gouge Roofing"
        />
      ),
    },
    {
      key: 3,
      logo: (
        <StaticImage src="../assets/images/dabella_logo.png" alt="DaBella" />
      ),
    },
    {
      key: 4,
      logo: (
        <StaticImage
          src="../assets/images/royalty-roofing_logo.png"
          alt="Royalty Roofing"
        />
      ),
    },
    {
      key: 5,
      logo: (
        <StaticImage
          src="../assets/images/feldco_logo.png"
          alt="Feldco Factory"
        />
      ),
    },
  ];

export const ESTIMATE_AND_CLOSE_RESOURCES: ILearningCardProps['card'][] = [
  {
    id: 1,
    description:
      "Easily estimate complex siding projects with the industry's only visual scoping tool.",
    title: 'ARTICLE',
    linkTitle: 'Read the article',
    linkTo: '/blog/hovers-estimation-solution-now-includes-siding/',
    titleColor: 'neutral.600',
    image: (
      <StaticImage
        placeholder="blurred"
        alt=""
        loading="lazy"
        src="../assets/images/estimate_resources_1.jpg"
      />
    ),
    mobileImage: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/estimate_resources_1.jpg"
      />
    ),
  },
  {
    id: 2,
    description: 'A uniform sales process increased DaBella’s profits by $2M+.',
    title: 'CASE STUDY',
    linkTitle: 'Read the case study',
    titleColor: 'neutral.600',
    linkTo: '/blog/how-estimates-improved-dabellas-bottom-line/',
    image: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/estimate_resources_2.jpg"
      />
    ),
    mobileImage: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/estimate_resources_2.jpg"
      />
    ),
  },
  {
    id: 3,
    description: 'How HOVER measurements power estimates, proposals.',
    title: 'VIDEO',
    linkTitle: 'Watch the video',
    linkTo: 'https://hover.wistia.com/medias/icg4mc2i2t',
    titleColor: 'neutral.600',
    image: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/estimate_resources_3.jpg"
      />
    ),
    mobileImage: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/estimate_resources_3.jpg"
      />
    ),
  },
  {
    id: 4,
    description: 'How to use our powerful ordering dashboard.',
    title: 'VIDEO',
    linkTitle: 'Watch the video',
    linkTo: 'https://hover.wistia.com/medias/4nre3z21cq',
    titleColor: 'neutral.600',
    image: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/estimate_resources_4.jpg"
      />
    ),
    mobileImage: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/estimate_resources_4.jpg"
      />
    ),
  },
];
